<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { formatTime } from '@chatwoot/utils';
import Logo from 'next/icon/Logo.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { accountId } = useAccount();

const currentUser = computed(() => getters.getCurrentUser.value || {});
const inboxesList = computed(() => getters['inboxes/getInboxes'].value || []);
const agentsList = computed(() => getters['agents/getAgents'].value || []);
const accountSummary = computed(() => getters.getAccountSummary.value || {});

const greetingMessage = computed(() => {
  const hours = new Date().getHours();
  let part = 'Morning';
  if (hours >= 12 && hours < 18) {
    part = 'Afternoon';
  } else if (hours >= 18) {
    part = 'Evening';
  }
  const name = currentUser.value?.name ? `, ${currentUser.value.name}` : '';
  return `Good ${part}${name}`;
});

onMounted(() => {
  if (agentsList.value.length === 0) {
    store.dispatch('agents/get');
  }
  if (inboxesList.value.length === 0) {
    store.dispatch('inboxes/get');
  }

  if (accountId.value) {
    const now = Math.floor(Date.now() / 1000);
    const thirtyDaysAgo = now - 30 * 86400;
    store.dispatch('fetchAccountSummary', {
      from: thirtyDaysAgo,
      to: now,
      type: 'account',
      id: accountId.value,
    });
  }
});

const conversationsCount = computed(
  () => accountSummary.value.conversations_count || 0
);
const incomingCount = computed(
  () => accountSummary.value.incoming_messages_count || 0
);
const outgoingCount = computed(
  () => accountSummary.value.outgoing_messages_count || 0
);
const avgResponseTime = computed(() => {
  const seconds = accountSummary.value.avg_first_response_time;
  return seconds ? formatTime(seconds) : '—';
});
const inboxesCount = computed(() => inboxesList.value.length);
const agentsCount = computed(() => agentsList.value.length);

const actionCards = computed(() => [
  {
    key: 'inboxes',
    title: t('ONBOARDING.ALL_CONVERSATION.TITLE'),
    description: t('ONBOARDING.ALL_CONVERSATION.DESCRIPTION'),
    to: 'settings_inbox_new',
    linkText: t('ONBOARDING.OVERVIEW.CONFIGURE_INBOXES'),
    icon: 'i-lucide-messages-square',
    badge:
      inboxesCount.value > 0
        ? t('ONBOARDING.OVERVIEW.CONNECTED_COUNT', {
            count: inboxesCount.value,
          })
        : t('ONBOARDING.OVERVIEW.SETUP_NEEDED'),
    badgeColor:
      inboxesCount.value > 0
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
        : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  },
  {
    key: 'teams',
    title: t('ONBOARDING.TEAM_MEMBERS.TITLE'),
    description: t('ONBOARDING.TEAM_MEMBERS.DESCRIPTION'),
    to: 'settings_teams_new',
    linkText: t('ONBOARDING.OVERVIEW.MANAGE_TEAM'),
    icon: 'i-lucide-users',
    badge:
      agentsCount.value > 0
        ? t('ONBOARDING.OVERVIEW.SPECIALISTS_COUNT', {
            count: agentsCount.value,
          })
        : t('ONBOARDING.OVERVIEW.ADD_AGENTS'),
    badgeColor:
      'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  },
  {
    key: 'canned',
    title: t('ONBOARDING.CANNED_RESPONSES.TITLE'),
    description: t('ONBOARDING.CANNED_RESPONSES.DESCRIPTION'),
    to: 'canned_list',
    linkText: t('ONBOARDING.OVERVIEW.CONFIGURE_TEMPLATES'),
    icon: 'i-lucide-zap',
    badge: t('ONBOARDING.OVERVIEW.QUICK_MACROS'),
    badgeColor:
      'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  },
  {
    key: 'labels',
    title: t('ONBOARDING.LABELS.TITLE'),
    description: t('ONBOARDING.LABELS.DESCRIPTION'),
    to: 'labels_list',
    linkText: t('ONBOARDING.OVERVIEW.MANAGE_TAGS'),
    icon: 'i-lucide-tags',
    badge: t('ONBOARDING.OVERVIEW.TRIAGE_RULES'),
    badgeColor:
      'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  },
]);
</script>

<template>
  <div class="w-full max-w-6xl mx-auto p-6 sm:p-8 font-inter overflow-y-auto">
    <!-- Header Hero Card -->
    <div
      class="mb-6 rounded-2xl p-6 sm:p-8 border border-[#125c4f]/20 bg-gradient-to-br from-[#125c4f]/10 via-[#fffdf8] to-[#125c4f]/5 dark:from-[#125c4f]/20 dark:via-n-surface-1 dark:to-[#125c4f]/10 shadow-xs"
    >
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <div class="flex items-center gap-2 mb-2.5">
            <div
              class="size-7 rounded-lg bg-[#125c4f] flex items-center justify-center text-white shadow-xs"
            >
              <Logo class="size-4" />
            </div>
            <span
              class="text-xs font-semibold uppercase tracking-wider text-[#125c4f] dark:text-[#2dd4bf] bg-[#125c4f]/10 dark:bg-[#125c4f]/20 px-2.5 py-0.5 rounded-full border border-[#125c4f]/20"
            >
              {{ $t('ONBOARDING.OVERVIEW.BADGE') }}
            </span>
            <span
              class="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800"
            >
              <span
                class="size-1.5 rounded-full bg-emerald-500 animate-pulse"
              />
              {{ $t('ONBOARDING.OVERVIEW.LIVE_ENGINE') }}
            </span>
          </div>
          <h1
            class="text-2xl sm:text-3xl font-bold text-n-slate-12 tracking-tight"
          >
            {{ greetingMessage }}
          </h1>
          <p class="text-n-slate-11 text-sm sm:text-base mt-1.5 max-w-2xl">
            {{ $t('ONBOARDING.OVERVIEW.TAGLINE_DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-2.5 flex-shrink-0">
          <router-link
            :to="{ name: 'settings_inbox_new' }"
            class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#125c4f] hover:bg-[#0e483e] transition-colors shadow-xs"
          >
            <span class="i-lucide-plus size-4" />
            {{ $t('ONBOARDING.OVERVIEW.CONNECT_CHANNEL') }}
          </router-link>
          <router-link
            :to="{ name: 'general_settings_index' }"
            class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-n-slate-12 bg-white dark:bg-n-surface-2 border border-n-weak hover:bg-n-alpha-1 transition-colors"
          >
            <span class="i-lucide-settings size-4" />
            {{ $t('ONBOARDING.OVERVIEW.SETTINGS') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Live Support Operational Metrics (Backend Data) -->
    <div class="mb-6">
      <div class="flex items-center justify-between mb-3">
        <h2
          class="text-sm font-semibold uppercase tracking-wider text-n-slate-11"
        >
          {{ $t('ONBOARDING.OVERVIEW.METRICS_TITLE') }}
        </h2>
        <span class="text-xs text-n-slate-10">
          {{ $t('ONBOARDING.OVERVIEW.LIVE_DATA') }}
        </span>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div
          class="p-4 rounded-xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs"
        >
          <span class="text-xs text-n-slate-10 block mb-1">
            {{ $t('ONBOARDING.OVERVIEW.CONVERSATIONS') }}
          </span>
          <span class="text-xl font-bold text-n-slate-12">
            {{ conversationsCount }}
          </span>
          <span class="text-[11px] text-n-slate-10 block mt-0.5">
            {{ $t('ONBOARDING.OVERVIEW.TOTAL_TICKETS') }}
          </span>
        </div>
        <div
          class="p-4 rounded-xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs"
        >
          <span class="text-xs text-n-slate-10 block mb-1">
            {{ $t('ONBOARDING.OVERVIEW.INBOUND_MESSAGES') }}
          </span>
          <span class="text-xl font-bold text-n-slate-12">
            {{ incomingCount }}
          </span>
          <span class="text-[11px] text-n-slate-10 block mt-0.5">
            {{ $t('ONBOARDING.OVERVIEW.FROM_VISITORS') }}
          </span>
        </div>
        <div
          class="p-4 rounded-xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs"
        >
          <span class="text-xs text-n-slate-10 block mb-1">
            {{ $t('ONBOARDING.OVERVIEW.AGENT_REPLIES') }}
          </span>
          <span class="text-xl font-bold text-n-slate-12">
            {{ outgoingCount }}
          </span>
          <span class="text-[11px] text-n-slate-10 block mt-0.5">
            {{ $t('ONBOARDING.OVERVIEW.SENT_BY_TEAM') }}
          </span>
        </div>
        <div
          class="p-4 rounded-xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs"
        >
          <span class="text-xs text-n-slate-10 block mb-1">
            {{ $t('ONBOARDING.OVERVIEW.FIRST_RESPONSE') }}
          </span>
          <span class="text-xl font-bold text-n-slate-12">
            {{ avgResponseTime }}
          </span>
          <span class="text-[11px] text-n-slate-10 block mt-0.5">
            {{ $t('ONBOARDING.OVERVIEW.AVERAGE_SPEED') }}
          </span>
        </div>
        <div
          class="p-4 rounded-xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs"
        >
          <span class="text-xs text-n-slate-10 block mb-1">
            {{ $t('ONBOARDING.OVERVIEW.ACTIVE_INBOXES') }}
          </span>
          <span class="text-xl font-bold text-n-slate-12">
            {{ inboxesCount }}
          </span>
          <span class="text-[11px] text-n-slate-10 block mt-0.5">
            {{ $t('ONBOARDING.OVERVIEW.CONNECTED') }}
          </span>
        </div>
        <div
          class="p-4 rounded-xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs"
        >
          <span class="text-xs text-n-slate-10 block mb-1">
            {{ $t('ONBOARDING.OVERVIEW.SUPPORT_AGENTS') }}
          </span>
          <span class="text-xl font-bold text-n-slate-12">
            {{ agentsCount }}
          </span>
          <span class="text-[11px] text-n-slate-10 block mt-0.5">
            {{ $t('ONBOARDING.OVERVIEW.ASSIGNED_STAFF') }}
          </span>
        </div>
      </div>
    </div>

    <!-- AI & Human Architecture Status -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div
        class="p-5 rounded-2xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs flex items-start gap-4"
      >
        <div
          class="size-10 rounded-xl bg-[#125c4f]/10 dark:bg-[#125c4f]/20 flex items-center justify-center flex-shrink-0 text-[#125c4f] dark:text-[#2dd4bf]"
        >
          <span class="i-lucide-bot size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-2 mb-1">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('ONBOARDING.OVERVIEW.AI_TITLE') }}
            </h3>
            <span
              class="text-[11px] font-semibold text-[#125c4f] dark:text-[#2dd4bf] bg-[#125c4f]/10 dark:bg-[#125c4f]/20 px-2 py-0.5 rounded-full border border-[#125c4f]/20"
            >
              {{ $t('ONBOARDING.OVERVIEW.AI_BADGE') }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 leading-relaxed">
            {{ $t('ONBOARDING.OVERVIEW.AI_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div
        class="p-5 rounded-2xl bg-white dark:bg-n-surface-2 border border-n-weak shadow-xs flex items-start gap-4"
      >
        <div
          class="size-10 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0 text-amber-600 dark:text-amber-400"
        >
          <span class="i-lucide-shield-check size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-2 mb-1">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('ONBOARDING.OVERVIEW.QUEUE_TITLE') }}
            </h3>
            <span
              class="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800"
            >
              {{ $t('ONBOARDING.OVERVIEW.QUEUE_BADGE') }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 leading-relaxed">
            {{ $t('ONBOARDING.OVERVIEW.QUEUE_DESCRIPTION') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Quick Operations Setup Hub (Replaces stock illustration cards) -->
    <div class="mb-8">
      <h2
        class="text-sm font-semibold uppercase tracking-wider text-n-slate-11 mb-3"
      >
        {{ $t('ONBOARDING.OVERVIEW.HUB_TITLE') }}
      </h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div
          v-for="card in actionCards"
          :key="card.key"
          class="p-5 rounded-2xl bg-white dark:bg-n-surface-2 border border-n-weak hover:border-[#125c4f]/40 transition-all duration-200 flex flex-col justify-between shadow-xs group"
        >
          <div>
            <div class="flex items-center justify-between gap-2 mb-3">
              <div
                class="size-10 rounded-xl bg-[#125c4f]/10 dark:bg-[#125c4f]/20 flex items-center justify-center text-[#125c4f] dark:text-[#2dd4bf] group-hover:bg-[#125c4f] group-hover:text-white transition-colors"
              >
                <span :class="card.icon" class="size-5" />
              </div>
              <span
                class="text-xs font-semibold px-2.5 py-0.5 rounded-full border"
                :class="card.badgeColor"
              >
                {{ card.badge }}
              </span>
            </div>
            <h3 class="text-base font-semibold text-n-slate-12 mb-1">
              {{ card.title }}
            </h3>
            <p class="text-xs sm:text-sm text-n-slate-11 leading-relaxed mb-4">
              {{ card.description }}
            </p>
          </div>
          <div>
            <router-link
              :to="{ name: card.to }"
              class="inline-flex items-center gap-1.5 text-sm font-semibold text-[#125c4f] dark:text-[#2dd4bf] hover:text-[#0e483e] group-hover:translate-x-0.5 transition-all"
            >
              <span>{{ card.linkText }}</span>
              <span class="i-lucide-arrow-right size-3.5" />
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="pt-6 border-t border-n-weak flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-n-slate-10"
    >
      <div class="flex items-center gap-2">
        <span class="font-semibold text-n-slate-12">
          {{ $t('ONBOARDING.OVERVIEW.FOOTER_BRAND') }}
        </span>
        <span class="size-1 rounded-full bg-n-slate-6 inline-block" />
        <span>{{ $t('ONBOARDING.OVERVIEW.FOOTER_TAGLINE') }}</span>
      </div>
      <div>
        <span>{{ $t('ONBOARDING.OVERVIEW.FOOTER_COPYRIGHT') }}</span>
      </div>
    </div>
  </div>
</template>
